import React from "react";
import Marquee from "react-fast-marquee";
import {
  SiHubspot,
  SiSalesforce,
  SiGoogleads,
  SiGoogleanalytics,
  SiMeta,
  SiSemrush,
  SiMailchimp,
  SiWordpress,
  SiZapier,
  SiSlack,
  SiNotion,
  SiAnthropic,
  SiOpenai,
  SiPerplexity,
} from "react-icons/si";
import { FaLinkedin } from "react-icons/fa";
import { Shield, Cpu, Sparkles, TerminalSquare } from "lucide-react";

const ROW_A = [
  { Icon: SiHubspot, label: "HubSpot" },
  { Icon: SiSalesforce, label: "Salesforce" },
  { Icon: SiGoogleads, label: "Google Ads" },
  { Icon: FaLinkedin, label: "LinkedIn Ads" },
  { Icon: SiMeta, label: "Meta Ads" },
  { Icon: SiGoogleanalytics, label: "GA4" },
  { Icon: SiSemrush, label: "Semrush" },
  { Icon: SiMailchimp, label: "Mailchimp" },
  { label: "Marketo" },
  { label: "6sense" },
  { label: "Rollworks" },
];

const ROW_B = [
  { Icon: SiAnthropic, label: "Claude" },
  { Icon: SiOpenai, label: "GPT-5" },
  { Icon: Cpu, label: "MCP" },
  { Icon: SiPerplexity, label: "Perplexity" },
  { Icon: TerminalSquare, label: "Cursor" },
  { Icon: Sparkles, label: "Agentic AI" },
  { Icon: SiWordpress, label: "WordPress" },
  { Icon: SiZapier, label: "Zapier" },
  { Icon: SiSlack, label: "Slack" },
  { Icon: SiNotion, label: "Notion" },
];

const INDUSTRIES = [
  { Icon: Shield, label: "Cybersecurity" },
  { label: "DevTools" },
  { label: "MarTech" },
  { label: "Enterprise SaaS" },
  { label: "FinTech" },
  { label: "Data & Analytics" },
  { label: "AI / ML" },
  { label: "HR Tech" },
];

function Chip({ Icon, label, dark = false }) {
  return (
    <div
      className={`mx-4 md:mx-8 flex items-center gap-3 py-4 ${
        dark ? "text-paper" : "text-ink"
      }`}
    >
      {Icon ? (
        <Icon className="w-6 h-6 md:w-7 md:h-7" />
      ) : (
        <span className="w-2 h-2 bg-signal" />
      )}
      <span className="font-heading font-bold text-xl md:text-3xl tracking-tight uppercase">
        {label}
      </span>
    </div>
  );
}

export default function Tools() {
  return (
    <section data-testid="tools-section" className="bg-ink text-paper py-14 md:py-20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-10 md:mb-14">
        <div className="flex items-center gap-4 mb-8 md:mb-10">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 04 — Toolchain</span>
          <span className="flex-1 h-px bg-paper/20" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-paper/60">Daily drivers</span>
        </div>
        <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.9] tracking-tightest max-w-4xl">
          A toolchain honed over <span className="text-signal">14+ years</span> of shipping campaigns —
          classic <span className="italic font-medium">and</span> AI-native.
        </h2>
      </div>

      <div className="marquee-mask">
        <Marquee gradient={false} speed={40} pauseOnHover>
          {ROW_A.map((t, i) => (
            <Chip key={i} {...t} dark />
          ))}
        </Marquee>
      </div>

      <div className="marquee-mask mt-4">
        <Marquee gradient={false} speed={50} direction="right" pauseOnHover>
          {ROW_B.map((t, i) => (
            <Chip key={i} {...t} dark />
          ))}
        </Marquee>
      </div>

      {/* Industries served */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16 md:mt-20 pt-10 border-t border-paper/20">
        <div className="flex items-center gap-4 mb-8">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">◆ Industries shipped</span>
          <span className="flex-1 h-px bg-paper/20" />
        </div>
        <div className="flex flex-wrap gap-3" data-testid="industries-list">
          {INDUSTRIES.map(({ Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-2 border border-paper/30 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] hover:border-signal hover:text-signal transition-colors"
            >
              {Icon ? <Icon size={14} /> : <span className="w-1.5 h-1.5 bg-signal" />}
              {label}
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-paper/20 pt-10">
        {[
          ["HubSpot · Marketo", "Architect & admin"],
          ["Salesforce", "RevOps & routing"],
          ["6sense · Rollworks", "Intent + ABM"],
          ["Claude · MCP", "Agentic workflows"],
        ].map(([k, v], i) => (
          <div key={i}>
            <div className="font-heading font-bold text-xl tracking-tight">{k}</div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-paper/60 mt-2">
              {v}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
